import { skipToken } from "@reduxjs/toolkit/query";
import {
  collectPlaybookCriteria,
  getOutcomeDefinitionFromPlaybook,
  playbookIdSchema,
} from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  buildTeammateDisplayNames,
} from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { ProjectDetailsTabPath } from "@repo/app-types/helpers/adminAppRoutes";
import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import type { ChatSuggestion } from "@repo/ui/chat/chatEmptyState";
import { useMatch } from "react-router";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useProjectByIdQuery } from "../../api/entities/project.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { appPaths } from "../../app/appPaths.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { usePlaybookIdParam } from "../../hooks/usePlaybookIdParam.js";
import { getPlaybookConfigSuggestions } from "./copilotPlaybookConfigSuggestions.js";
import { getSignalConfigSuggestions } from "./copilotSignalConfigSuggestions.js";

/** Shown when no route produces more specific suggestions. */
const fallbackSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize playbook performance",
    prompt:
      "Summarize the team's playbook performance over the past two weeks. Highlight the strongest and weakest criteria with examples.",
  },
  {
    label: "Find coaching risks",
    prompt:
      "Which teammates show coaching risks based on their recent calls? Include specific patterns and transcript examples.",
  },
  {
    label: "Research a topic",
    prompt:
      "What are the most common themes customers bring up in recent calls? Summarize the top topics with examples.",
  },
];

/** Starters for the knowledge base document editor, carried over from the page's retired assistant. */
const knowledgeBaseDocumentSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize this article",
    prompt:
      "Summarize the knowledge base document I'm viewing, and tell me what a rep would struggle to find in it.",
  },
  {
    label: "Improve clarity and structure",
    prompt:
      "Review the knowledge base document I'm viewing for clarity and structure, and propose the edits that would help a rep most on a live call.",
  },
  {
    label: "Check recent adherence findings",
    prompt:
      "Have recent calls produced knowledge base adherence findings against the document I'm viewing? If so, show me whether the document or the expectation is what needs to change.",
  },
];

/**
 * The message the Instructions tab's Test button sends: a check of the saved
 * draft, with no candidate, whose row opens in the tab's Results panel.
 */
export function buildTestInstructionsPrompt(agentName: string): string {
  return `Test ${agentName}'s instruction sections: run the automated instruction checks on the saved draft as it is, then give me a brief takeaway of what failed.`;
}

/**
 * Contextual prompt suggestions for the copilot's empty state, keyed off the
 * current route (mirroring the pre-app-shell suggestion pills).
 */
export function useCopilotSuggestions(): ChatSuggestion[] {
  // Match route families to determine context
  const teammateRouteMatch = useMatch(`${appPaths.teammate.pattern}/*`);
  const teammateAgentRouteMatch = useMatch(
    `${appPaths.teammateAgent.pattern}/*`,
  );

  // Match specific team page views
  const byCustomerViewMatch = useMatch(`${appPaths.playbookCalls.pattern}/*`);
  const byPlaybookViewMatch = useMatch(appPaths.playbookCriteria.pattern);
  const byCriterionViewMatch = useMatch(appPaths.playbookCriterion.pattern);

  // The playbook configuration surface: the editor and its tabs, matched
  // separately from the team playbook views above.
  const configEditorMatch = useMatch(`${appPaths.settingsPlaybook.pattern}/*`);

  // The training builder. Its route is the project setup tab, shared with
  // recruiting, so the project itself decides whether these starters apply.
  const projectSetupMatch = useMatch(
    `${appPaths.project.pattern}/${ProjectDetailsTabPath.ProjectConfiguration}/*`,
  );
  const { data: setupProject } = useProjectByIdQuery(
    projectSetupMatch?.params.projectId ?? skipToken,
  );

  // The signal editor: `new` is matched first because the `:signalId` pattern
  // also matches it.
  const newSignalEditorMatch = useMatch(appPaths.settingsNewSignal.pattern);
  const signalEditorMatch = useMatch(appPaths.settingsSignal.pattern);

  // The knowledge base document editor, which carries the open document as
  // page context, so these starters never have to name it.
  const knowledgeBaseDocumentMatch = useMatch(
    appPaths.settingsKnowledgeBaseAttachment.pattern,
  );

  // Extract IDs from route matches. Teammate URLs nest under the playbook, so
  // the playbook hook covers both route families.
  const playbookId = usePlaybookIdParam();
  const teammateId = teammateRouteMatch?.params.teammateId
    ? asTeammateId(teammateRouteMatch.params.teammateId)
    : undefined;
  const teammateAgentId = teammateAgentRouteMatch?.params.teammateAgentId;
  const criterionId = byCriterionViewMatch?.params.criterionId;

  // Fetch data for contextual suggestions
  const { data: teammate } = useBasicTeammateQuery(teammateId ?? skipToken);
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const { data: teammateAgent } = useGetTeammateAgentQuery(
    teammateAgentId ?? skipToken,
  );

  const outcomeDefinition = playbook
    ? getOutcomeDefinitionFromPlaybook(playbook)
    : undefined;

  // The playbook configuration surface (editor + tabs) gets playbook-shaping
  // starters — draft an outcome, draft or extend the playbook, surface KB gaps —
  // in place of the generic research prompts. A not-yet-loaded playbook reads as
  // empty, which is the "draft it" case, so a brand-new playbook still gets them.
  const configPlaybookId = playbookIdSchema.safeParse(
    configEditorMatch?.params.playbookId,
  ).data;
  const { data: configPlaybook } = usePlaybookByIdQuery(
    configPlaybookId ?? skipToken,
  );
  const isKnowledgeBaseEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.KnowledgeBase,
  );
  if (newSignalEditorMatch || signalEditorMatch) {
    return getSignalConfigSuggestions({
      isNewSignal: newSignalEditorMatch !== null,
    });
  }

  if (setupProject && getIsTrainingProject(setupProject)) {
    return [
      {
        label: "Review this training",
        prompt:
          "Review this training's chapters and lessons for gaps and improvements, then propose the changes you recommend.",
      },
      {
        label: "Add a chapter",
        prompt:
          "Add a chapter to this training covering a topic the team struggles with on recent calls. Include practice lessons.",
      },
      {
        label: "Improve a role play",
        prompt:
          "Review this training's role-play lessons for realism, simulate the weakest one, and propose better instructions.",
      },
    ];
  }

  if (knowledgeBaseDocumentMatch) {
    return knowledgeBaseDocumentSuggestions;
  }

  if (configEditorMatch) {
    return getPlaybookConfigSuggestions({
      playbookTree: configPlaybook?.assessment_tree,
      outcomeDefinition: configPlaybook
        ? getOutcomeDefinitionFromPlaybook(configPlaybook)
        : undefined,
      isKnowledgeBaseEnabled: isKnowledgeBaseEnabled ?? false,
    });
  }

  if (teammateId && teammate) {
    const teammateDisplayNames = buildTeammateDisplayNames(teammate);

    // Show secondary display name, since email may help distinguish teammates
    const teammateName = teammateDisplayNames.secondaryDisplayName
      ? `${teammateDisplayNames.primaryDisplayName} (${teammateDisplayNames.secondaryDisplayName})`
      : teammateDisplayNames.primaryDisplayName;

    // User is reviewing an individual rep — likely preparing for coaching or
    // investigating performance
    const suggestions: ChatSuggestion[] = [
      {
        label: "Prepare coaching notes",
        prompt: `Prepare actionable coaching notes for ${teammateName} based on their calls from the past 2 weeks. Include specific strengths to reinforce and areas for improvement with examples and transcript quotes.`,
      },
      {
        label: "Pull transcript highlights",
        prompt: `Find transcript highlights and lowlights for ${teammateName} from the past few weeks. Include specific quotes that demonstrate both strong performance and coaching opportunities.`,
      },
    ];

    if (outcomeDefinition) {
      const outcomeName = outcomeDefinition.name.toLowerCase();
      suggestions.push({
        label: `Analyze their ${outcomeName} rate`,
        prompt: `Analyze why the ${outcomeName} rate for ${teammateName} is where it is. What specific behaviors or patterns in their calls are contributing to this? What could they do differently?`,
      });
    }

    return suggestions;
  }

  if (playbookId && !teammateId) {
    if (byCustomerViewMatch) {
      // Customer interactions view — users are looking at customer calls
      return [
        {
          label: "Find common objections",
          prompt:
            "What are the most common objections customers are raising in recent calls? Include specific examples and how reps are handling them.",
        },
        {
          label: "Review negative calls",
          prompt:
            "Which recent calls had negative customer sentiment? Summarize what went wrong and identify patterns.",
        },
        {
          label: "Compile positive quotes",
          prompt:
            "Compile positive customer quotes from recent calls. Include specific quotes that demonstrate what the team is doing well.",
        },
      ];
    }

    if (byCriterionViewMatch && criterionId) {
      // Criterion drill-down — users are looking at a specific criterion
      const criterionName = playbook
        ? collectPlaybookCriteria(playbook.assessment_tree).find(
            (criterion) => criterion.id === criterionId,
          )?.name
        : undefined;

      if (criterionName) {
        return [
          {
            label: "Prepare coaching notes for this criteria",
            prompt: `Prepare actionable coaching notes for the team on the "${criterionName}" criteria. Include specific examples from recent calls and recommendations for improvement.`,
          },
        ];
      }
    }

    if (byPlaybookViewMatch) {
      // Criteria adherence view
      const suggestions: ChatSuggestion[] = [
        {
          label: "What are the biggest improvement opportunities?",
          prompt:
            "Which playbook criteria is the team struggling with most? Provide specific examples and recommendations for improvement.",
        },
      ];

      if (outcomeDefinition) {
        const outcomeName = outcomeDefinition.name.toLowerCase();
        suggestions.push({
          label: `What's impacting ${outcomeName} most?`,
          prompt: `Which playbook criteria have the biggest impact on ${outcomeName}? Analyze the correlation between criteria adherence and outcomes.`,
        });
      }

      return suggestions;
    }

    return fallbackSuggestions;
  }

  if (teammateAgentId) {
    // User is configuring or reviewing a voice agent: performance research,
    // or the instruction, simulation and test case tools.
    const agentName = teammateAgent?.name ?? "this agent";
    return [
      {
        label: "Improve the instructions",
        prompt: `Review ${agentName}'s instruction sections for gaps, conflicts and missing guardrails, then propose the changes you recommend.`,
      },
      {
        label: "Test the instructions",
        prompt: buildTestInstructionsPrompt(agentName),
      },
      {
        label: "Simulate a call",
        prompt: `Simulate a typical customer calling ${agentName} and tell me where the agent could do better.`,
      },
      {
        label: "Propose test cases",
        prompt: `Review ${agentName}'s configuration and propose test cases that cover its main call paths.`,
      },
    ];
  }

  return fallbackSuggestions;
}
